import React, { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Box, Flex, Text, Loader, Transition } from "@mantine/core";
import { useSelector } from "react-redux";
import { IconX, IconCheck, IconChevronRight } from "@tabler/icons-react";
import useDrawerStyles from "./style";
import ProviderPicker, { providerLabel } from "./components/ProviderPicker";
import ClientChecklist from "./components/ClientChecklist";
import RangePickerField from "./components/RangePickerField";
import TaraAssistantPanel from "./TaraAssistantPanel";
import { initials, formatRange, QUARTERS } from "../../pages/Home/QuarterlyReportsPage/utils";
import { QR } from "../../pages/Home/QuarterlyReportsPage/theme";
import { searchSkypointProviders } from "../../services/skypointProvider";
import {
  getClientsForGenerate,
  getQuarterPresets,
  startGeneration,
  getGenerationProgress,
  retryGenerationRow,
} from "../../services/quarterlyReport";

const RING_C = 169.6; // 2*pi*27, r=27 to match the prototype's ring geometry
const POLL_INTERVAL_MS = 2500;
const TERMINAL_STATUSES = ["ready", "failed"];

const ProgressRing = ({ done, total, classes }) => {
  const offset = RING_C * (1 - (total ? done / total : 0));
  return (
    <Box className={classes.progRingWrap}>
      <svg width={62} height={62} className={classes.progRingSvg}>
        <circle cx={31} cy={31} r={27} fill="none" stroke={QR.line} strokeWidth={6} />
        <circle
          cx={31}
          cy={31}
          r={27}
          fill="none"
          stroke={QR.purple}
          strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={RING_C}
          strokeDashoffset={offset}
          className={classes.progRingCircleFill}
        />
      </svg>
      <Box className={classes.progRingLabel}>
        <span className={classes.progRingDone}>{done}</span>
        <span className={classes.progRingTotal}>/{total}</span>
      </Box>
    </Box>
  );
};

// The providers this feature may list: only ones approved for this contractor (ticket
// requirement; the backend's own assertApprovedProvider() enforces the same rule, this just
// keeps the UI from offering a dead end), and only Ogiso ones. External providers document in
// their own EHR, so there are no approved Ogiso notes for Tara Ai to draft from: the backend
// rejects them outright (see assertOgisoProvider), and a manual report is the path for those.
// Create › Quarterly Report covers that. Both are filtered on the server: filtering one page
// in the browser left approved providers past it out (OD-1694).
const GENERATE_PROVIDERS = { providerType: "ogiso", approval: "approved" };

const GenerateDrawer = ({ opened, onClose, initialProviderId, initialProviderName, onReviewReports }) => {
  const { classes, cx } = useDrawerStyles();

  const { clientsForProvider, clientsLoading, generationBatch } = useSelector((state) => state.quarterlyReport);

  const [pane, setPane] = useState("setup");
  const [providerId, setProviderId] = useState(initialProviderId || null);
  const [providerName, setProviderName] = useState(initialProviderName || "");
  // The first page of providers, for the default pick and the "none yet" note; null until loaded
  const [firstProviders, setFirstProviders] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [period, setPeriod] = useState({ from: QUARTERS[1].from, to: QUARTERS[1].to });
  // The Review Period rail's preset list — starts as the static calendar-year
  // fallback, replaced with the real GET /quarter-presets result (which
  // reflects the "Quarter basis" setting: calendar, or ISP-plan-year
  // anchored to the selected provider's clients) once it loads.
  const [quarters, setQuarters] = useState(QUARTERS);
  // Set once the user picks a period themselves, so applying the "Default
  // review period" setting (which refetches whenever the provider changes)
  // can never overwrite a deliberate choice.
  const periodTouchedRef = useRef(false);
  const handlePeriodChange = (next) => {
    periodTouchedRef.current = true;
    setPeriod(next);
  };
  const pollRef = useRef(null);
  // Tracks which provider the current selection was defaulted for — lets us
  // tell "provider actually changed" (re-default to select-all) apart from
  // "just refetched for a new period" (keep whatever the user picked).
  const selectionDefaultedForRef = useRef(null);

  const providersLoading = opened && firstProviders === null;

  useEffect(() => {
    if (opened) {
      setProviderId(initialProviderId || null);
      setProviderName(initialProviderName || "");
      setPane("setup");
      selectionDefaultedForRef.current = null; // fresh session — next client load selects all again
      periodTouchedRef.current = false; // fresh session — re-apply the default review period
    } else {
      clearInterval(pollRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Interval cleanup on unmount too — not just on close — so navigating away
  // from the page mid-generation doesn't leave the poller running.
  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    if (!opened) return undefined;
    let cancelled = false;
    setFirstProviders(null);
    searchSkypointProviders("", GENERATE_PROVIDERS).then((result) => {
      if (!cancelled) setFirstProviders(result);
    });
    return () => {
      cancelled = true;
    };
  }, [opened]);

  // once providers load, default to the first one if nothing was preset. Provider records come
  // through the backend's toJSON plugin, which renames _id to id (and drops _id).
  useEffect(() => {
    const first = firstProviders?.providers?.[0];
    if (opened && !providerId && first) {
      setProviderId(first.id);
      setProviderName(providerLabel(first));
    }
  }, [opened, providerId, firstProviders]);

  // Refetches whenever the provider changes (ISP-plan-year anchoring, if
  // that's the active "Quarter basis" setting, depends on that provider's
  // clients' own plan start dates) — falls back to the static calendar-year
  // list on failure rather than leaving the rail empty.
  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    getQuarterPresets(providerId).then((result) => {
      if (cancelled) return;
      const presets = result?.presets?.length ? result.presets : QUARTERS;
      setQuarters(presets);
      // Which preset to open on comes from the "Default review period" setting
      // (resolved server-side, since only it knows where the quarters fall —
      // an ISP plan year rarely lines up with the calendar one). Applied only
      // while the setup pane is untouched, so re-anchoring the rail for a newly
      // picked provider never overwrites a period the user chose themselves.
      const fallbackIndex = presets === QUARTERS ? 1 : presets.length - 1;
      const index = Number.isInteger(result?.defaultIndex) ? result.defaultIndex : fallbackIndex;
      const preset = presets[index] || presets[0];
      if (preset && !periodTouchedRef.current) setPeriod({ from: preset.from, to: preset.to });
    });
    return () => {
      cancelled = true;
    };
  }, [opened, providerId]);

  useEffect(() => {
    if (!opened || !providerId) return;
    getClientsForGenerate(providerId, null, period.from, period.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, providerId, period.from, period.to]);

  // Re-selects everyone only when the provider actually changes (a new
  // client set) — a refetch for the same provider (e.g. after applying a new
  // Review Period from the range picker) keeps the user's existing picks
  // instead of silently reselecting all of them.
  useEffect(() => {
    const ids = (clientsForProvider || []).map((c) => c.id);
    if (selectionDefaultedForRef.current !== providerId) {
      setSelectedIds(ids);
      selectionDefaultedForRef.current = providerId;
    } else {
      setSelectedIds((prev) => prev.filter((id) => ids.includes(id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsForProvider]);

  const toggleClient = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = (on) => setSelectedIds(on ? (clientsForProvider || []).map((c) => c.id) : []);

  const periodLabel = useMemo(() => formatRange(period.from, period.to), [period]);

  const rows = generationBatch?.rows || [];
  const doneCount = rows.filter((r) => r.status === "ready").length;
  const generateDisabled = selectedIds.length === 0;

  const stopPollingIfSettled = (currentRows) => {
    if (currentRows.length && currentRows.every((r) => TERMINAL_STATUSES.includes(r.status))) {
      clearInterval(pollRef.current);
    }
  };

  const pollProgress = (batchId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const data = await getGenerationProgress(batchId);
      if (data?.rows) stopPollingIfSettled(data.rows);
    }, POLL_INTERVAL_MS);
  };

  const [starting, setStarting] = useState(false);
  const handleStartGeneration = async () => {
    setStarting(true);
    const data = await startGeneration(providerId, null, selectedIds, period.from, period.to);
    setStarting(false);
    if (data?.batchId) {
      setPane("progress");
      pollProgress(data.batchId);
    }
  };

  const [retryingClientId, setRetryingClientId] = useState(null);
  const handleRetryRow = async (clientId) => {
    if (!generationBatch?.batchId) return;
    setRetryingClientId(clientId);
    await retryGenerationRow(generationBatch.batchId, clientId);
    setRetryingClientId(null);
    pollProgress(generationBatch.batchId);
  };

  const openReview = (startReportId) => {
    const readyReportIds = rows.filter((r) => r.status === "ready").map((r) => r.reportId);
    clearInterval(pollRef.current);
    onClose();
    onReviewReports(
      readyReportIds,
      startReportId || readyReportIds[0],
      providerId ? { id: providerId, name: providerName } : undefined,
      periodLabel
    );
  };

  return (
    // Compound API (Drawer.Root/.Overlay/.Content), not the <Drawer> shorthand
    // — deliberately, so the Tara Ai panel below can be added as a sibling of
    // .Content instead of nested inside it. Mantine's page-scroll lock
    // (react-remove-scroll) only recognizes wheel events as "inside" a locked
    // drawer when their target is a genuine DOM descendant of this root; the
    // old structure rendered TaraAssistantPanel as an external sibling AFTER
    // </Drawer> closed, so it sat outside that boundary and its scroll got
    // silently blocked. As a sibling of .Content (not nested inside it), it's
    // a real descendant of the same lock — while staying outside .Content's
    // own slide-transition transform, so its position: fixed still resolves
    // against the true viewport rather than that animated panel.
    <Drawer.Root
      opened={opened}
      onClose={onClose}
      position="right"
      size={620}
      padding={0}
      zIndex={1000}
      styles={{
        content: { borderRadius: 16, display: "flex", flexDirection: "column" },
        body: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0, padding: 0 },
      }}
    >
      <Drawer.Overlay opacity={0.55} blur={2} />
      <Drawer.Content>
        <Drawer.Body>
          {pane === "setup" ? (
          <Box className={classes.paneCol}>
            <Flex className={classes.paneHead}>
              <Text size={20} weight={700}>
                Generate Quarterly Reports
              </Text>
              <button className={classes.drawerX} onClick={onClose} aria-label="Close">
                <IconX size={20} />
              </button>
            </Flex>

            <Box className={classes.paneBody}>
              <Box className={classes.wizSection}>
                <Flex className={classes.wizHead}>
                  <span className={classes.stepNum}>1</span>
                  <Text className={classes.wizLabel}>Provider</Text>
                </Flex>
                <ProviderPicker
                  value={providerId}
                  selectedLabel={providerName}
                  providerType={GENERATE_PROVIDERS.providerType}
                  onSelect={(opt) => {
                    setProviderId(opt.value);
                    setProviderName(opt.label);
                  }}
                  placeholder={providersLoading ? "Loading providers…" : "Select a provider"}
                />
                {!providersLoading && firstProviders?.count === 0 && (
                  <Text size="xs" color="dimmed" mt={6}>
                    No approved Ogiso providers linked to your account yet.
                  </Text>
                )}
              </Box>

              <Box className={classes.wizSection}>
                <Flex className={classes.wizHead}>
                  <span className={classes.stepNum}>2</span>
                  <Text className={classes.wizLabel}>Clients</Text>
                </Flex>
                {clientsLoading ? (
                  <Flex justify="center" py={20}>
                    <Loader size="sm" color="violet" />
                  </Flex>
                ) : (
                  <ClientChecklist
                    clients={clientsForProvider || []}
                    selected={selectedIds}
                    onToggle={toggleClient}
                    onToggleAll={toggleAll}
                  />
                )}
              </Box>

              <Box className={classes.wizSection}>
                <Flex className={classes.wizHead}>
                  <span className={classes.stepNum}>3</span>
                  <Text className={classes.wizLabel}>Review Period</Text>
                </Flex>
                <RangePickerField value={period} onChange={handlePeriodChange} quarters={quarters} />
              </Box>
            </Box>

            <Flex className={classes.paneFoot}>
              <button className={classes.btnGhost} onClick={onClose}>
                Cancel
              </button>
              <button
                className={classes.btnAdd}
                disabled={generateDisabled || starting}
                onClick={handleStartGeneration}
              >
                <Flex className={classes.btnInner}>
                  {starting && <Loader size={13} color="currentColor" />}
                  {starting
                    ? "Starting…"
                    : `Generate ${selectedIds.length} Report${selectedIds.length === 1 ? "" : "s"}`}
                </Flex>
              </button>
            </Flex>
          </Box>
        ) : (
          <Box className={classes.paneCol}>
            <Flex className={classes.paneHead}>
              <Text size={20} weight={700}>
                Generating reports
              </Text>
              <button className={classes.drawerX} onClick={onClose} aria-label="Close">
                <IconX size={20} />
              </button>
            </Flex>

            <Box className={classes.paneBody}>
              <Flex className={classes.progSummary}>
                <ProgressRing done={doneCount} total={rows.length} classes={classes} />
                <Box>
                  <Text className={classes.progSummaryTitle}>Generating {rows.length} reports</Text>
                  <Text className={classes.progSummaryMeta}>
                    {providerName || "Provider"} · {periodLabel}
                  </Text>
                </Box>
              </Flex>

              {rows.map((r) => {
                const chipClass = {
                  ready: classes.statusChipReady,
                  generating: classes.statusChipGenerating,
                  waiting: classes.statusChipWaiting,
                  failed: classes.statusChipFailed,
                }[r.status];
                const label = {
                  ready: "Ready for review",
                  generating: "Generating",
                  waiting: "Waiting",
                  failed: "Failed",
                }[r.status];
                // A "failed" row with a reportId is the duplicate-report case
                // (backend already has a report for this client+period) — that
                // one links straight to the existing report instead of retrying.
                const isDuplicate = r.status === "failed" && !!r.reportId;
                const clickable = r.status === "ready" || isDuplicate;
                return (
                  <Flex
                    key={r.clientId}
                    className={cx(classes.progRow, clickable && classes.progRowClickable)}
                    onClick={() => clickable && openReview(r.reportId)}
                  >
                    <Box className={classes.progAv}>{initials(r.name)}</Box>
                    <Box className={classes.progNameWrap}>
                      <Text className={classes.clientName}>{r.name}</Text>
                      {r.status === "failed" && (
                        <Text className={classes.errMeta}>{r.errorMessage || "Generation failed. The draft was not saved."}</Text>
                      )}
                    </Box>
                    <Flex align="center" gap={10}>
                      <span className={cx(classes.statusChip, chipClass)}>
                        {r.status === "ready" && <IconCheck size={11} />}
                        {r.status === "generating" && <span className={classes.spinner} />}
                        {label}
                      </span>
                      {r.status === "failed" && !isDuplicate && (
                        <button
                          className={classes.rowBtnDanger}
                          disabled={retryingClientId === r.clientId}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetryRow(r.clientId);
                          }}
                        >
                          <Flex className={classes.retryBtnInner}>
                            {retryingClientId === r.clientId && <Loader size={11} color="currentColor" />}
                            {retryingClientId === r.clientId ? "Retrying…" : "Retry"}
                          </Flex>
                        </button>
                      )}
                      {clickable && <IconChevronRight size={16} color={QR.muted} />}
                    </Flex>
                  </Flex>
                );
              })}
            </Box>

            <Flex className={classes.paneFoot}>
              <button className={classes.btnGhost} onClick={() => setPane("setup")}>
                Back
              </button>
              <button className={classes.btnAdd} disabled={doneCount === 0} onClick={() => openReview()}>
                Review Reports
              </button>
            </Flex>
          </Box>
        )}
        </Drawer.Body>
      </Drawer.Content>

      {/* A sibling of Drawer.Content (see the note above <Drawer.Root>), not
          nested inside it — that's what keeps this panel's own scroll working
          and its position: fixed anchored to the real viewport. It still
          needs the drawer's own slide-left/250ms transition so it doesn't pop
          into place while the drawer is still sliding in. */}
      <Transition mounted={opened} transition="slide-left" duration={250} timingFunction="ease">
        {(transitionStyle) => (
          <TaraAssistantPanel
            style={transitionStyle}
            state={pane}
            clientCount={rows.length || selectedIds.length}
            providerId={providerId}
            startDate={period.from}
            endDate={period.to}
            rows={rows}
            batchId={generationBatch?.batchId}
          />
        )}
      </Transition>
    </Drawer.Root>
  );
};

export default GenerateDrawer;
